/**
 * Life4Earth V2 — Pre-Flight Items API Adapter
 *
 * Endpoint: /api/PreflightItems
 *
 * Provides typed fetch functions and shape converters for the pre-flight
 * checklist data. Hydrates the centralized store on successful load.
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";
import type { PreflightItem } from "../Data/PreFlight/preflightTypes";
import { normalizeCategory, normalizePhase } from "../Data/PreFlight/preflightTypes";

// ── API Response Types ──────────────────────────────────────

export interface PreflightItemResponse {
  id: string;
  projectId: string;
  phase: string;
  category: string;
  label: string;
  description: string | null;
  requirementType: string;
  targetQuantity: number | null;
  unit: string | null;
  currentQuantity: number;
  isFulfilled: boolean;
  visibility: string;
  missionSubkpiKey: string | null;
  tags: string[];
  sortOrder: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  sourceTemplateGuid: string | null;
  dependsOn?: string[];
  // State machine fields (migration 102)
  // DEV-963: optional during v1.2 transition — pre-migration API rows
  // and template-derived items may not carry them. Mirrors the same
  // optionality on PreflightItem in preflightTypes.ts.
  state?: string;
  priority?: string;
  descriptorTags?: string[];
  matchingConditions?: string | null;
  linkedOfferGuid?: string | null;
  withdrawnAt?: string | null;
  // Cost / Budget fields (migrations 025 + 026) — same v1.2 transition rule.
  costCategory?: string;
  unitCost?: number | null;
  actualCost?: number | null;
  costCurrency?: string | null;
  costNote?: string | null;
  estimatedTotal?: number | null;
  actualTotal?: number | null;
  // EVM-Lite fields (migration 027) — same v1.2 transition rule.
  ganttTaskId?: string | null;
  percentComplete?: number;
  // Fulfillment / Acquisition fields (DEV-647) — same v1.2 transition rule.
  fulfillmentNote?: string | null;
  assigneeGuid?: string | null;
}

// ── Shape Converter ─────────────────────────────────────────

/**
 * Convert API response to the internal PreflightItem shape
 * used by the store and UI components.
 */
export function apiResponseToPreflightItem(
  r: PreflightItemResponse
): PreflightItem {
  return {
    item_guid: normalizeGuid(r.id),
    project_guid: normalizeGuid(r.projectId),
    phase: normalizePhase(r.phase),
    category: normalizeCategory(r.category),
    label: r.label,
    description: r.description ?? undefined,
    requirement_type: r.requirementType as PreflightItem["requirement_type"],
    target_quantity: r.targetQuantity,
    unit: r.unit,
    current_quantity: r.currentQuantity,
    is_fulfilled: r.isFulfilled,
    // State machine fields
    state: (r.state || (r.isFulfilled ? "met" : "seeking")) as PreflightItem["state"],
    priority: (r.priority || "important") as PreflightItem["priority"],
    descriptor_tags: (r.descriptorTags || []) as PreflightItem["descriptor_tags"],
    matching_conditions: r.matchingConditions ? JSON.parse(r.matchingConditions) : null,
    linked_offer_guid: r.linkedOfferGuid ? normalizeGuid(r.linkedOfferGuid) : null,
    withdrawn_at: r.withdrawnAt ?? null,
    visibility: r.visibility as PreflightItem["visibility"],
    mission_subkpi_key: r.missionSubkpiKey,
    tags: r.tags || [],
    sort_order: r.sortOrder,
    created_by: normalizeGuid(r.createdBy),
    created_at: r.createdAt,
    updated_at: r.updatedAt,
    source_template_guid: r.sourceTemplateGuid ? normalizeGuid(r.sourceTemplateGuid) : null,
    depends_on: r.dependsOn?.map((g: string) => g.toUpperCase()) ?? [],
    // Cost / Budget fields
    cost_category: normalizeCategory(r.costCategory),
    unit_cost: r.unitCost ?? null,
    actual_cost: r.actualCost ?? null,
    cost_currency: r.costCurrency ?? null,
    cost_note: r.costNote ?? null,
    estimated_total: r.estimatedTotal ?? null,
    actual_total: r.actualTotal ?? null,
    // EVM-Lite fields
    gantt_task_id: r.ganttTaskId ? normalizeGuid(r.ganttTaskId) : null,
    percent_complete: r.percentComplete ?? 0,
    // Fulfillment / Acquisition fields (DEV-647)
    fulfillment_note: r.fulfillmentNote ?? null,
    assignee_guid: r.assigneeGuid ? normalizeGuid(r.assigneeGuid) : null,
  };
}

// ── API Functions ───────────────────────────────────────────

/**
 * Fetch all pre-flight items across all projects.
 * GET /api/PreflightItems
 *
 * apiFetch<T> handles response.ok checks and JSON parsing internally,
 * throwing ApiError on non-2xx responses.
 */
export async function fetchAllPreflightItems(): Promise<PreflightItemResponse[]> {
  return apiFetch<PreflightItemResponse[]>("/api/PreflightItems");
}

/**
 * Fetch pre-flight items for a specific project.
 * GET /api/PreflightItems?projectId={guid}
 */
export async function fetchPreflightItemsByProject(
  projectGuid: string
): Promise<PreflightItemResponse[]> {
  return apiFetch<PreflightItemResponse[]>(
    `/api/PreflightItems?projectId=${encodeURIComponent(projectGuid)}`
  );
}

/**
 * Create or update a pre-flight item.
 * PUT /api/PreflightItems/{id}
 */
export async function upsertPreflightItem(
  item: PreflightItem
): Promise<PreflightItemResponse> {
  return apiFetch<PreflightItemResponse>(`/api/PreflightItems/${item.item_guid}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      projectId: item.project_guid,
      phase: item.phase,
      category: item.category,
      label: item.label,
      description: item.description || null,
      requirementType: item.requirement_type,
      targetQuantity: item.target_quantity,
      unit: item.unit,
      currentQuantity: item.current_quantity,
      isFulfilled: item.state === "met",
      visibility: item.visibility,
      missionSubkpiKey: item.mission_subkpi_key || null,
      tags: item.tags || [],
      sortOrder: item.sort_order,
      dependsOn: item.depends_on ?? [],
      // State machine fields
      state: item.state,
      priority: item.priority,
      descriptorTags: item.descriptor_tags || [],
      matchingConditions: item.matching_conditions ? JSON.stringify(item.matching_conditions) : null,
      // Cost fields
      costCategory: normalizeCategory(item.cost_category),
      unitCost: item.unit_cost ?? null,
      actualCost: item.actual_cost ?? null,
      costCurrency: item.cost_currency ?? null,
      costNote: item.cost_note ?? null,
      // EVM-Lite fields
      ganttTaskId: item.gantt_task_id || null,
      percentComplete: item.percent_complete ?? 0,
      // Fulfillment / Acquisition fields (DEV-647)
      fulfillmentNote: item.fulfillment_note || null,
      assigneeGuid: item.assignee_guid || null,
    }),
  });
}

/**
 * Batch create/update pre-flight items in a single round-trip.
 * POST /api/PreflightItems/batch
 *
 * Used when applying templates or importing items.
 */
export async function batchUpsertPreflightItems(
  items: PreflightItem[]
): Promise<PreflightItemResponse[]> {
  return apiFetch<PreflightItemResponse[]>("/api/PreflightItems/batch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      items: items.map((item) => ({
        itemGuid: item.item_guid,
        projectId: item.project_guid,
        phase: item.phase,
        category: item.category,
        label: item.label,
        description: item.description || null,
        requirementType: item.requirement_type,
        targetQuantity: item.target_quantity,
        unit: item.unit,
        currentQuantity: item.current_quantity,
        isFulfilled: item.state === "met",
        visibility: item.visibility,
        missionSubkpiKey: item.mission_subkpi_key || null,
        tags: item.tags || [],
        sortOrder: item.sort_order,
        sourceTemplateGuid: item.source_template_guid || null,
        dependsOn: item.depends_on ?? [],
        // State machine fields
        state: item.state,
        priority: item.priority,
        descriptorTags: item.descriptor_tags || [],
        matchingConditions: item.matching_conditions ? JSON.stringify(item.matching_conditions) : null,
        // Cost fields
        costCategory: normalizeCategory(item.cost_category),
        unitCost: item.unit_cost ?? null,
        actualCost: item.actual_cost ?? null,
        costCurrency: item.cost_currency ?? null,
        costNote: item.cost_note ?? null,
        // EVM-Lite fields
        ganttTaskId: item.gantt_task_id || null,
        percentComplete: item.percent_complete ?? 0,
        // Fulfillment / Acquisition fields (DEV-647)
        fulfillmentNote: item.fulfillment_note || null,
        assigneeGuid: item.assignee_guid || null,
      })),
    }),
  });
}

/**
 * Delete a pre-flight item.
 * DELETE /api/PreflightItems/{id}
 */
export async function deletePreflightItem(itemGuid: string): Promise<void> {
  return apiFetch<void>(`/api/PreflightItems/${itemGuid}`, {
    method: "DELETE",
  });
}
